import { useEffect, useRef, useState, useContext } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api/api";
import "./CoursePlayer.css";
import StreakFreezeModal from "../../components/StreakFreezeModal/StreakFreezeModal";
import CourseTimeEstimator from "../../components/CourseTimeEstimator/CourseTimeEstimator";

function CoursePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const isPro = user?.subscription?.status === "ACTIVE";

  const context = location.state?.from || null;
  const trackSlug = location.state?.trackSlug || null;
  const phaseId = location.state?.phaseId || null;

  const playerRef = useRef(null);
  const intervalRef = useRef(null);

  const [course, setCourse] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [resumeTime, setResumeTime] = useState(0);
  const [progressPercent, setProgressPercent] = useState(0);
  const [progressData, setProgressData] = useState(null);
  const [nextItem, setNextItem] = useState(null);

  // ================= AI QUIZ STATE =================
  const [activeTab, setActiveTab] = useState("notes");
  const [quiz, setQuiz] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({});
  const [quizResults, setQuizResults] = useState(null);
  const [submittingQuiz, setSubmittingQuiz] = useState(false);

  // ================= NOTES STATE =================
  const [notes, setNotes] = useState([]);
  const [savingNotes, setSavingNotes] = useState(false);
  const saveTimeoutRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  //streakFreeze
  const [showStreakFreezeModal, setShowStreakFreezeModal] = useState(false);
  //eslint-disable-next-line
  const [freezeCredits, setFreezeCredits] = useState(0);

  // ================= FETCH COURSE =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const courseRes = await api.get(`/courses/${id}`);
        const courseData = courseRes.data.course;
        setCourse(courseData);

        const progressRes = await api.get(`/progress/continue/${id}`);

        if (progressRes.data?.videoId) {
          setProgressData(progressRes.data);
          setCurrentVideo(progressRes.data.videoId);
          setResumeTime(progressRes.data.time || 0);
          setProgressPercent(progressRes.data.progressPercent || 0);
        } else {
          setCurrentVideo(courseData?.videos?.[0]?.videoId || null);
        }

        setLoading(false);
      } catch {
        setError("Failed to load course.");
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  // ================= FETCH VIDEO PROGRESS ON SWITCH =================
  useEffect(() => {
    if (!currentVideo) return;

    const fetchVideoProgress = async () => {
      try {
        const res = await api.get(`/progress/continue/${id}`);

        const videoProgress = res.data?.perVideo?.find(
          (v) => v.videoId === currentVideo
        );

        if (videoProgress) {
          setResumeTime(videoProgress.time || 0);
          setNotes(videoProgress.notes || []);
        } else {
          setResumeTime(0);
          setNotes([]);
        }
      } catch {}
    };

    fetchVideoProgress();
    // Reset quiz state when switching videos
    setQuiz(null);
    setQuizStarted(false);
    setQuizResults(null);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
  }, [currentVideo, id]);

  // ================= NEXT ITEM LOGIC =================
  useEffect(() => {
    if (!id) return;

    const fetchNext = async () => {
      try {
        let currentContext = context;
        let currentTrackSlug = trackSlug;

        // Auto-detect context if missing (e.g. on refresh)
        if (!currentContext) {
          try {
            const ctxRes = await api.get(`/progress/context/${id}`);
            if (ctxRes.data.context) {
              currentContext = ctxRes.data.context;
              currentTrackSlug = ctxRes.data.trackSlug;
              // We don't set local state here to avoid loops, 
              // just use for the next fetch
            }
          } catch (e) {
            // ignore context fetch error
          }
        }

        const query = new URLSearchParams();
        if (currentContext) query.append("from", currentContext);
        if (currentTrackSlug) query.append("trackSlug", currentTrackSlug);
        if (phaseId) query.append("phaseId", phaseId);

        const res = await api.get(`/progress/next/${id}?${query.toString()}`);
        setNextItem(res.data);
      } catch (err) {
        console.error("Failed to fetch next item:", err);
      }
    };
    fetchNext();
  }, [id, context, trackSlug, phaseId]);

  // ================= YOUTUBE PLAYER =================
  useEffect(() => {
    if (!course || !currentVideo) return;

    const loadPlayer = () => {
      if (playerRef.current) playerRef.current.destroy();

      playerRef.current = new window.YT.Player("yt-player", {
        videoId: currentVideo,
        playerVars: {
          rel: 0,
          modestbranding: 1,
          showinfo: 0,
          controls: 1,
          fs: 1,
          disablekb: 0,
          playsinline: 1,
        },
        events: {
          onReady: (event) => {
            const player = event.target;
            if (resumeTime > 0) {
              setTimeout(() => {
                try { player.seekTo(resumeTime, true); } catch {}
              }, 400);
            }
          },
          onStateChange: handleStateChange,
        },
      });
    };

    if (!window.YT || !window.YT.Player) {
      const existingScript = document.querySelector(
        'script[src="https://www.youtube.com/iframe_api"]'
      );
      if (!existingScript) {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        document.body.appendChild(tag);
      }
      window.onYouTubeIframeAPIReady = () => { loadPlayer(); };
    } else {
      loadPlayer();
    }

    return () => {
      stopTracking();
      if (playerRef.current) playerRef.current.destroy();
    };
    // eslint-disable-next-line
  }, [course, currentVideo]);

  // ================= TRACKING LOGIC =================
  const handleStateChange = (event) => {
    const state = event.data;
    if (state === window.YT.PlayerState.PLAYING) startTracking();
    if (state === window.YT.PlayerState.PAUSED || state === window.YT.PlayerState.BUFFERING) stopTracking();
    if (state === window.YT.PlayerState.ENDED) {
      stopTracking();
      updateProgress();
      goToNextVideo();
    }
  };

  const startTracking = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(updateProgress, 8000);
  };

  const stopTracking = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const updateProgress = async () => {
    if (!playerRef.current) return;
    const watchedSec = playerRef.current.getCurrentTime();
    const durationSec = playerRef.current.getDuration();
    try {
      const res = await api.post("/progress/update", {
        courseId: id,
        videoId: currentVideo,
        watchedSec,
        durationSec,
      });
      if (res.data?.overallPercent !== undefined) setProgressPercent(res.data.overallPercent);
      if (res.data?.perVideo) {
        setProgressData((prev) => ({ ...prev, perVideo: res.data.perVideo }));
      }
    } catch {}
  };

  // ================= NAVIGATION =================
  const getCurrentIndex = () =>
    course?.videos?.findIndex((v) => v.videoId === currentVideo);

  const currentIndex = getCurrentIndex();
  const currentVideoData = course?.videos?.[currentIndex];

  const goToNextVideo = () => {
    if (currentIndex < course.videos.length - 1) {
      setResumeTime(0);
      setCurrentVideo(course.videos[currentIndex + 1].videoId);
    }
  };

  const goToPrevVideo = () => {
    if (currentIndex > 0) {
      setResumeTime(0);
      setCurrentVideo(course.videos[currentIndex - 1].videoId);
    }
  };

  // ================= NOTES LOGIC =================
  const debounceSave = (updatedNotes) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveNotes(updatedNotes), 900);
  };

  const saveNotes = async (notesToSave) => {
    try {
      setSavingNotes(true);
      await api.patch("/progress/notes", { courseId: id, videoId: currentVideo, notes: notesToSave });
      setSavingNotes(false);
    } catch { setSavingNotes(false); }
  };

  const formatTime = (sec) => {
    const minutes = Math.floor(sec / 60);
    const seconds = Math.floor(sec % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const addTimestampNote = () => {
    if (!playerRef.current) return;
    setNotes((prev) => [...prev, { timeSec: playerRef.current.getCurrentTime(), content: "" }]);
  };

  const addNormalNote = () => {
    setNotes((prev) => [...prev, { timeSec: null, content: "" }]);
  };

  const updateNoteContent = (index, value) => {
    const updated = [...notes];
    updated[index].content = value;
    setNotes(updated);
    debounceSave(updated);
  };

  const deleteNote = (index) => {
    const updated = notes.filter((_, i) => i !== index);
    setNotes(updated);
    debounceSave(updated);
  };

  const handleStartQuiz = async () => {
    if (!isPro) return;
    try {
      setQuizLoading(true);
      const res = await api.get(`/quiz/${currentVideo}?title=${encodeURIComponent(currentVideoData?.title || "")}`);
      setQuiz(res.data);
      setQuizStarted(true);
      setQuizLoading(false);
    } catch (err) {
      console.error(err);
      setQuizLoading(false);
    }
  };

  const handleSelectOption = (questionIndex, optionIndex) => {
    setUserAnswers(prev => ({ ...prev, [questionIndex]: optionIndex }));
  };

  const handleSubmitQuiz = async () => {
    try {
      setSubmittingQuiz(true);
      const answersArray = Object.entries(userAnswers).map(([qi, oi]) => ({
        questionIndex: parseInt(qi),
        selectedOption: oi
      }));

      const res = await api.post("/quiz/submit", {
        quizId: quiz.quizId,
        videoId: currentVideo,
        answers: answersArray
      });

      setQuizResults(res.data);
      setSubmittingQuiz(false);
    } catch (err) {
      console.error(err);
      setSubmittingQuiz(false);
    }
  };

  const handleRetakeQuiz = () => {
    setQuizResults(null);
    setQuizStarted(false);
    setCurrentQuestionIndex(0);
    setUserAnswers({});
  };

  // const handleContinueRoadmap = async () => {
  //   try {
  //     const res = await api.get("/roadmap/current-course/");
  //     if (res.data.type === "course" && res.data.courseId) navigate(`/course/${res.data.courseId}`);
  //     if (res.data.type === "project" && res.data.projectId) navigate(`/project`);
  //   } catch (err) { console.error(err); }
  // };

  // ================= MOTIVATION =================
  const previousVideoId = currentIndex > 0 ? course?.videos?.[currentIndex - 1]?.videoId : null;
  const previousVideoProgress = progressData?.perVideo?.find((v) => v.videoId === previousVideoId);
  const previousIncomplete = previousVideoId && previousVideoProgress && !previousVideoProgress.completed;

  // ================= RENDER =================
  return loading ? (
    <div className="course-loading">Loading...</div>
  ) : error ? (
    <div className="course-error">{error}</div>
  ) : (
    <div className="course-player">

      <div className="player-section">

        <div className="top-bar">
          <button className="back-btn" onClick={() => navigate("/dashboard")}>
            ← Back to Dashboard
          </button>
          <div className="nav-mini">
            <button onClick={goToPrevVideo} disabled={currentIndex === 0}>Prev</button>
            <button onClick={goToNextVideo} disabled={currentIndex === course?.videos?.length - 1}>Next</button>
          </div>
        </div>

        {/* ── Context Banner ───────────────────────────── */}
        {nextItem && context === "track" && (
          <div className="course-context-banner">
            <span className="context-label">Learning Track: {nextItem.trackName}</span>
          </div>
        )}
        {context === "roadmap" && (
          <div className="course-context-banner roadmap">
            <span className="context-label">Personalized Roadmap Journey</span>
          </div>
        )}

        <div id="yt-player" className="video-frame"></div>

        <div className="course-meta">
          <h2>{currentVideoData?.title}</h2>
          {currentIndex === 0 && progressPercent === 0 ? (
            <p className="motivation">🚀 Start strong. This is your first step.</p>
          ) : previousIncomplete ? (
            <p className="motivation warning">⚠ You didn't complete the previous lesson.</p>
          ) : (
            <p>{progressPercent}% completed</p>
          )}
        </div>

        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: progressPercent === 0 ? "6%" : `${progressPercent}%` }}
          />
        </div>



        {/* ── Journey Continuation ──────────────────────── */}
        {progressPercent === 100 && nextItem?.next && (
          <div className="journey-continuation">
            <div className="journey-card">
              <div className="journey-info">
                <h3>{nextItem.next.type === 'phase_complete' ? 'Phase Completed!' : 'Ready for the next step?'}</h3>
                <p>{nextItem.next.message || nextItem.next.title}</p>
              </div>
              <button 
                className="journey-btn"
                onClick={() => {
                  if (nextItem.next.type === 'course') {
                    navigate(`/course/${nextItem.next.courseId}`, { state: location.state });
                  } else if (nextItem.next.type === 'project') {
                    navigate(`/project/${nextItem.next.projectId}`);
                  } else if (nextItem.next.type === 'phase_complete') {
                    navigate(`/tracks/${nextItem.next.trackSlug}`);
                  }
                }}
              >
                {nextItem.next.type === 'phase_complete' ? 'Back to Track' : 'Continue Journey →'}
              </button>
            </div>
          </div>
        )}

        {/* ── Study Planner ─────────────────────────────── */}
        <CourseTimeEstimator
          course={course}
          progressPercent={progressPercent}
        />

        <div className="stats-row">
          <div className="stat-card">
            <span>Total Videos</span>
            <h4>{course?.videos?.length}</h4>
          </div>
          <div className="stat-card">
            <span>Completed</span>
            <h4>{progressPercent}%</h4>
          </div>
          <div className="stat-card roadmap">
            <span>AI Insights</span>
            <h4>🔒 Coming Soon</h4>
          </div>
        </div>

        <div className="interactive-panel">
          <div className="interaction-tabs">
            <button 
              className={`tab-btn ${activeTab === "notes" ? "active" : ""}`}
              onClick={() => setActiveTab("notes")}
            >
              📝 Notes
            </button>
            <button 
              className={`tab-btn ${activeTab === "quiz" ? "active" : ""}`}
              onClick={() => setActiveTab("quiz")}
            >
              ✨ AI Quiz
            </button>
          </div>

          <div className="tab-content">
            {activeTab === "notes" ? (
              <div className="notes-panel">
                <div className="notes-header">
                  <h4>Study Notes</h4>
                  <div className="notes-actions">
                    <button onClick={addTimestampNote}>+ Timestamp</button>
                    <button onClick={addNormalNote}>+ Note</button>
                  </div>
                  <div className="notes-status">
                    {savingNotes ? "Saving..." : "Saved ✓"}
                  </div>
                </div>

                {notes.length === 0 ? (
                  <div className="notes-empty">📝 No notes yet. Capture insights while learning.</div>
                ) : (
                  <div className="notes-list">
                    {notes.map((note, index) => (
                      <div key={index} className="note-item">
                        {note.timeSec !== null && (
                          <span
                            className="note-time"
                            onClick={() => playerRef.current.seekTo(note.timeSec)}
                          >
                            {formatTime(note.timeSec)}
                          </span>
                        )}
                        <textarea
                          value={note.content}
                          placeholder="Write your thoughts..."
                          onChange={(e) => updateNoteContent(index, e.target.value)}
                        />
                        <button className="note-delete" onClick={() => deleteNote(index)}>✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="quiz-container">
                {!isPro ? (
                  <div className="quiz-intro">
                    <div className="ai-quiz-locked">
                      🔒 AI Quizzes are a Pro Feature
                    </div>
                    <p style={{ marginTop: '1rem' }}>Upgrade to unlock personalized assessments for every video.</p>
                    <button className="start-quiz-btn" onClick={() => navigate("/upgrade")}>Upgrade Now</button>
                  </div>
                ) : quizLoading ? (
                  <div className="quiz-intro">Generating AI Quiz for this lesson...</div>
                ) : !quizStarted ? (
                  <div className="quiz-intro">
                    <h3>Ready for a quick check?</h3>
                    <p>Test your understanding of "{currentVideoData?.title}"</p>
                    <button className="start-quiz-btn" onClick={handleStartQuiz}>Start Quiz</button>
                  </div>
                ) : quizResults ? (
                  <div className="quiz-results">
                    <div className={`results-score-circle ${!quizResults.passed ? 'failed' : ''}`}>
                      <span className="score-num">{quizResults.score}%</span>
                      <span className="score-label">{quizResults.passed ? "PASSED" : "TRY AGAIN"}</span>
                    </div>
                    <div className="results-summary">
                      <h3>{quizResults.passed ? "Great Job!" : "Keep Practicing"}</h3>
                      <p>You got {quizResults.correctCount} out of {quizResults.totalQuestions} correct.</p>
                    </div>

                    {!quizResults.passed && (
                      <div className="recommendation-card">
                        <span>💡</span>
                        <div className="recommendation-info">
                          <h4>Review Recommended</h4>
                          <p>Rewatch the video to clarify key concepts before retaking.</p>
                        </div>
                      </div>
                    )}

                    <div className="results-breakdown">
                      {quizResults.results.map((res, i) => (
                        <div key={i} className={`result-item ${res.correct ? 'correct' : ''}`}>
                          <div className="result-status">{res.correct ? "✓ Correct" : "✕ Incorrect"}</div>
                          <div className="result-q">{res.q}</div>
                          {res.explanation && <div className="result-explanation">{res.explanation}</div>}
                        </div>
                      ))}
                    </div>

                    <div className="retake-footer">
                      <button className="retake-btn" onClick={handleRetakeQuiz}>Retake Quiz</button>
                    </div>
                  </div>
                ) : quiz ? (
                  <div className="quiz-view">
                    <div className="quiz-header">
                      <span className="quiz-progress-text">Question {currentQuestionIndex + 1} of {quiz.questions.length}</span>
                    </div>
                    <div className="question-card">
                      <div className="question-text">{quiz.questions[currentQuestionIndex].q}</div>
                      <div className="options-list">
                        {quiz.questions[currentQuestionIndex].options.map((opt, i) => (
                          <button 
                            key={i} 
                            className={`option-btn ${userAnswers[currentQuestionIndex] === i ? 'selected' : ''}`}
                            onClick={() => handleSelectOption(currentQuestionIndex, i)}
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="quiz-nav">
                      <button 
                        className="prev-q-btn" 
                        disabled={currentQuestionIndex === 0}
                        onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                      >
                        Previous
                      </button>
                      {currentQuestionIndex < quiz.questions.length - 1 ? (
                        <button 
                          className="next-q-btn"
                          disabled={userAnswers[currentQuestionIndex] === undefined}
                          onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                        >
                          Next
                        </button>
                      ) : (
                        <button 
                          className="submit-quiz-btn"
                          disabled={Object.keys(userAnswers).length < quiz.questions.length || submittingQuiz}
                          onClick={handleSubmitQuiz}
                        >
                          {submittingQuiz ? "Submitting..." : "Finish Quiz"}
                        </button>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="playlist-section">
        <div className="playlist-header">
          <h4>Course Content</h4>
          <span>{course?.videos?.length} lessons</span>
        </div>
        <div className="playlist-list">
          {course?.videos?.map((video, index) => {
            const videoProgress = progressData?.perVideo?.find((v) => v.videoId === video.videoId);
            const isCompleted = videoProgress?.completed;
            return (
              <div
                key={video.videoId}
                className={`playlist-item ${video.videoId === currentVideo ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                onClick={() => { setResumeTime(0); setCurrentVideo(video.videoId); }}
              >
                <span className="video-count">
                  {isCompleted ? "✔" : String(index + 1).padStart(2, "0")}
                </span>
                <span className="video-title">{video.title}</span>
              </div>
            );
          })}
        </div>
      </div>

      {showStreakFreezeModal && (
        <StreakFreezeModal
          freezeCredits={freezeCredits}
          onClose={() => setShowStreakFreezeModal(false)}
          onResolved={() => {}}
        />
      )}
    </div>
  );
}

export default CoursePlayer;